// @systeme: sondages
// ============================================================================
// PairsForm — Page publique de réponse à une demande de disponibilité
// ============================================================================
// Route : /disponibilites/repondre/:token  (pas d'authentification)
//
// Mode PILOTE_PROPOSE : le formateur répond dispo/indispo sur chaque date.
// Mode FORMATEUR_PROPOSE : le formateur ajoute ses propres dates disponibles.
// ============================================================================

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API_URL } from "../lib/api";
import {
  Box, Typography, Card, CardContent, Button, CircularProgress,
  Alert, Chip, Stack, Divider, Paper, TextField, MenuItem, IconButton,
} from "@mui/material";
import { Check, X, Calendar, Send, CheckCircle, Plus } from "lucide-react";
import { formatDateFR, formatDateJourFR } from "@cecodev/shared";
import ChampDate from "../components/ChampDate";


// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface DateDispo {
  id: string;
  date: string;
  periode: "JOURNEE" | "MATIN" | "APRES_MIDI";
  reponse: "DISPONIBLE" | "INDISPONIBLE" | null;
  ordre: number;
}

interface DemandeSummary {
  id: string;
  categorie: string;
  mode: string;
  commentaire: string | null;
  statut: string;
  flux: "PILOTE_PROPOSE" | "FORMATEUR_PROPOSE";
  plageDebut: string | null;
  plageFin: string | null;
  animateurPrenom: string;
  animateurNom: string;
  pilotePrenom: string;
  piloteNom: string;
}

interface NewDateEntry {
  date: string;
  periode: "JOURNEE" | "MATIN" | "APRES_MIDI";
}

// ────────────────────────────────────────────────────────────────────────────
// Constantes
// ────────────────────────────────────────────────────────────────────────────

const CATEGORIE_LABELS: Record<string, string> = {
  ANIMATION: "Animation",
  FORMATION: "Formation",
  SUPERVISION: "Supervision",
  AUTRE: "Autre",
};

const MODE_LABELS: Record<string, string> = {
  PRESENTIEL: "Présentiel",
  DISTANCIEL: "Distanciel",
  HYBRIDE: "Hybride",
};

const PERIODE_LABELS: Record<string, string> = {
  JOURNEE: "Journée entière",
  MATIN: "Matin",
  APRES_MIDI: "Après-midi",
};

const PERIODE_OPTIONS = [
  { value: "JOURNEE", label: "Journée entière" },
  { value: "MATIN", label: "Matin" },
  { value: "APRES_MIDI", label: "Après-midi" },
];

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export function RepondreDisponibilitePage() {
  const { token } = useParams<{ token: string }>();
  const [demande, setDemande] = useState<DemandeSummary | null>(null);
  const [dates, setDates] = useState<DateDispo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Mode PILOTE_PROPOSE : map dateId → réponse
  const [reponses, setReponses] = useState<Record<string, "DISPONIBLE" | "INDISPONIBLE">>({});

  // Mode FORMATEUR_PROPOSE : liste de dates ajoutées par le formateur
  const [newDates, setNewDates] = useState<NewDateEntry[]>([{ date: "", periode: "JOURNEE" }]);

  useEffect(() => {
    const fetchDemande = async () => {
      try {
        const res = await fetch(`${API_URL}/demandes-disponibilite/repondre/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error ?? "Lien invalide ou demande introuvable.");
          return;
        }
        const data = await res.json();
        setDemande(data.demande);
        setDates(data.dates);

        // Pré-remplir les réponses déjà données (PILOTE_PROPOSE)
        const preRempli: Record<string, "DISPONIBLE" | "INDISPONIBLE"> = {};
        for (const d of data.dates) {
          if (d.reponse) preRempli[d.id] = d.reponse;
        }
        setReponses(preRempli);

        // Pré-remplir les dates déjà soumises (FORMATEUR_PROPOSE)
        if (data.demande.flux === "FORMATEUR_PROPOSE" && data.dates.length > 0) {
          setNewDates(
            data.dates.map((d: DateDispo) => ({ date: d.date, periode: d.periode }))
          );
        }
      } catch {
        setError("Erreur de connexion. Veuillez réessayer.");
      } finally {
        setLoading(false);
      }
    };
    fetchDemande();
  }, [token]);

  // ── Handlers PILOTE_PROPOSE ──────────────────────────────────────────────

  const setReponse = (dateId: string, val: "DISPONIBLE" | "INDISPONIBLE") => {
    setReponses((prev) => ({ ...prev, [dateId]: val }));
  };

  const handleSubmitPilotePropose = async () => {
    if (Object.keys(reponses).length < dates.length) {
      alert("Veuillez répondre à toutes les dates avant de valider.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/demandes-disponibilite/repondre/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reponses }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "Erreur lors de l'envoi.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Handlers FORMATEUR_PROPOSE ───────────────────────────────────────────

  const addNewDate = () => setNewDates([...newDates, { date: "", periode: "JOURNEE" }]);
  const removeNewDate = (i: number) => setNewDates(newDates.filter((_, idx) => idx !== i));
  const updateNewDate = (i: number, field: keyof NewDateEntry, value: string) => {
    setNewDates(newDates.map((d, idx) => idx === i ? { ...d, [field]: value } : d));
  };

  const handleSubmitFormateurPropose = async () => {
    const validDates = newDates.filter((d) => d.date);
    if (validDates.length === 0) {
      alert("Veuillez ajouter au moins une date disponible.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/demandes-disponibilite/repondre/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dates: validDates }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "Erreur lors de l'envoi.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = dates.length > 0 && dates.every((d) => reponses[d.id]);
  const hasValidNewDate = newDates.some((d) => d.date);

  // ── Rendu ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={520} mx="auto" mt={6} px={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box maxWidth={520} mx="auto" mt={6} px={2} textAlign="center">
        <CheckCircle size={56} color="#4caf50" style={{ marginBottom: 16 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Merci pour votre réponse !
        </Typography>
        <Typography color="text.secondary">
          {demande?.flux === "FORMATEUR_PROPOSE"
            ? "Vos dates disponibles ont bien été enregistrées. Le pilote en sera notifié."
            : "Vos disponibilités ont bien été enregistrées. Le pilote en sera notifié."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: "#f5f5f5", py: 4, px: 2 }}>
      <Box maxWidth={560} mx="auto">
        {/* En-tête */}
        <Paper
          sx={{
            background: "#E1512C",
            color: "#fff",
            p: 3,
            borderRadius: "8px 8px 0 0",
            textAlign: "center",
          }}
        >
          <Calendar size={32} style={{ marginBottom: 8 }} />
          <Typography variant="h5" fontWeight={700}>
            Demande de disponibilité
          </Typography>
        </Paper>

        <Card sx={{ borderRadius: "0 0 8px 8px" }}>
          <CardContent sx={{ p: 3 }}>
            {demande && (
              <>
                <Typography variant="body1" gutterBottom>
                  Bonjour <strong>{demande.animateurPrenom}</strong>,
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  <strong>{demande.pilotePrenom} {demande.piloteNom}</strong> vous demande vos disponibilités
                  pour une intervention.
                </Typography>

                {/* Infos demande */}
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                  <Chip label={CATEGORIE_LABELS[demande.categorie] ?? demande.categorie} size="small" />
                  <Chip label={MODE_LABELS[demande.mode] ?? demande.mode} size="small" variant="outlined" />
                  {demande.commentaire && (
                    <Chip label={demande.commentaire} size="small" variant="outlined" color="primary" />
                  )}
                </Stack>

                {demande.statut === "REPONDUE" && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Vous avez déjà répondu à cette demande. Vous pouvez modifier vos réponses ci-dessous.
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* ── Mode PILOTE_PROPOSE ────────────────────────────────── */}
                {demande.flux === "PILOTE_PROPOSE" && (
                  <>
                    <Typography variant="subtitle2" mb={1.5}>
                      Pour chaque date, indiquez votre disponibilité :
                    </Typography>
                    <Stack spacing={1.5}>
                      {dates.map((d) => {
                        const rep = reponses[d.id];
                        return (
                          <Box
                            key={d.id}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: rep === "DISPONIBLE" ? "success.main" : rep === "INDISPONIBLE" ? "error.main" : "divider",
                              background: rep === "DISPONIBLE" ? "rgba(76,175,80,0.06)" : rep === "INDISPONIBLE" ? "rgba(244,67,54,0.06)" : "transparent",
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatDateJourFR(d.date)}
                                </Typography>
                                {d.periode !== "JOURNEE" && (
                                  <Typography variant="caption" color="text.secondary">
                                    {PERIODE_LABELS[d.periode]}
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant={rep === "DISPONIBLE" ? "contained" : "outlined"}
                                  color="success"
                                  startIcon={<Check size={14} />}
                                  onClick={() => setReponse(d.id, "DISPONIBLE")}
                                >
                                  Disponible
                                </Button>
                                <Button
                                  size="small"
                                  variant={rep === "INDISPONIBLE" ? "contained" : "outlined"}
                                  color="error"
                                  startIcon={<X size={14} />}
                                  onClick={() => setReponse(d.id, "INDISPONIBLE")}
                                >
                                  Indisponible
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>

                    <Box mt={3} textAlign="center">
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmitPilotePropose}
                        disabled={!allAnswered || submitting}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                        sx={{ px: 4 }}
                      >
                        Valider mes disponibilités
                      </Button>
                      {!allAnswered && (
                        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                          Répondez à toutes les dates pour valider.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}

                {/* ── Mode FORMATEUR_PROPOSE ─────────────────────────────── */}
                {demande.flux === "FORMATEUR_PROPOSE" && (
                  <>
                    {/* Plage demandée */}
                    {demande.plageDebut && demande.plageFin && (
                      <Alert severity="info" icon={<Calendar size={18} />} sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          Plage souhaitée : <strong>{formatDateFR(demande.plageDebut)}</strong>
                          {" → "}
                          <strong>{formatDateFR(demande.plageFin)}</strong>
                        </Typography>
                      </Alert>
                    )}

                    <Typography variant="subtitle2" mb={0.5}>
                      Indiquez vos dates disponibles dans cette période :
                    </Typography>
                    <Typography variant="caption" color="text.secondary" mb={2} display="block">
                      Ajoutez autant de dates que vous êtes disponible. Le pilote confirmera ensuite les dates qui lui conviennent.
                    </Typography>

                    <Stack spacing={1.5}>
                      {newDates.map((d, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="center">
                          <ChampDate fullWidth={false} min={demande.plageDebut ?? undefined} max={demande.plageFin ?? undefined}
                            label="Date"
                            value={d.date}
                            onChange={(v) => updateNewDate(i, "date", v)}
                            size="small"
                            sx={{ flex: 2 }}
                          />
                          <TextField
                            select
                            label="Créneau"
                            value={d.periode}
                            onChange={(e) => updateNewDate(i, "periode", e.target.value as any)}
                            size="small"
                            sx={{ flex: 1.5 }}
                          >
                            {PERIODE_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                          </TextField>
                          <IconButton
                            size="small"
                            disabled={newDates.length === 1}
                            onClick={() => removeNewDate(i)}
                            color="error"
                          >
                            <X size={14} />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Plus size={14} />}
                      onClick={addNewDate}
                      sx={{ mt: 1.5, mb: 3 }}
                    >
                      Ajouter une date
                    </Button>

                    <Box textAlign="center">
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmitFormateurPropose}
                        disabled={!hasValidNewDate || submitting}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                        sx={{ px: 4 }}
                      >
                        Envoyer mes disponibilités
                      </Button>
                      {!hasValidNewDate && (
                        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                          Ajoutez au moins une date disponible pour valider.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
          PairsForm · Intelligence collective
        </Typography>
      </Box>
    </Box>
  );
}

export default RepondreDisponibilitePage;
